import type { NotifyPayload, SubscriptionTerminatedPayload } from '../model/types';
import { PARK_ACCEPT, PARK_EVENT, parseDialogInfoXml } from './call-parking-utils';
import {
	applyConferenceInfoPatch,
	CONFERENCE_ACCEPT,
	CONFERENCE_EVENT,
} from './conference-info-utils';
import type { JsSIPAdapter } from './jssip-adapter';
import { applyRegPatch, REG_ACCEPT, REG_EVENT } from './reg-utils';

/**
 * Регистрирует обработчики event-package для парковки звонков.
 *
 * @param adapter Экземпляр JsSIPAdapter.
 */
export function registerCallParkingPackage(adapter: JsSIPAdapter) {
	adapter.registerEventPackage(PARK_EVENT, {
		accept: PARK_ACCEPT,
		onNotify(meta, payload: NotifyPayload) {
			const xml = payload.body ?? payload.request.body ?? '';
			const dialogs = parseDialogInfoXml(xml);

			adapter.emit('parkDialogsUpdated', dialogs, payload.isFinal, meta.target);
		},
		onTerminated(meta, info: SubscriptionTerminatedPayload) {
			adapter.emit('onParkFailed', info, meta.target);
		},
	});
}

/**
 * Регистрирует обработчики event-package для конференций (Event: conference).
 *
 * @param adapter Экземпляр JsSIPAdapter.
 */
export function registerConferencePackage(adapter: JsSIPAdapter) {
	adapter.registerEventPackage(CONFERENCE_EVENT, {
		accept: CONFERENCE_ACCEPT,
		onNotify(meta, payload: NotifyPayload) {
			const xml = payload.body ?? payload.request.body ?? '';
			const prev = adapter.getConferenceSnapshot(meta.target);
			const next = applyConferenceInfoPatch(prev, xml, meta.target);

			if (!next) return;

			adapter.setConferenceSnapshot(meta.target, next, payload);
		},
		onTerminated(meta, info: SubscriptionTerminatedPayload) {
			console.info(info);
			adapter.setConferenceSnapshot(meta.target, null);
		},
	});
}

/**
 * Регистрирует обработчики event-package reginfo.
 *
 * @param adapter Экземпляр JsSIPAdapter.
 */
export function registerRegPackage(adapter: JsSIPAdapter) {
	adapter.registerEventPackage(REG_EVENT, {
		accept: REG_ACCEPT,

		// Обновляем локальный snapshot и пробрасываем сырой reginfo NOTIFY для совместимости.
		onNotify(meta, payload: NotifyPayload, source: any) {
			const { isFinal, request, body, contentType } = payload;
			// TODO Упростить получение getHeader
			const resolvedContentType = contentType ?? request.getHeader('content-type');
			if (!/application\/reginfo\+xml/i.test(resolvedContentType ?? '')) return;

			const xml = body ?? request.body ?? '';
			if (!xml) return;

			const prev = adapter.getRegistrationSnapshot(meta.target);
			const next = applyRegPatch(prev, xml);

			if (prev && next.version <= prev.version) return;

			adapter.setRegistrationSnapshot(meta.target, next); // публичный setter, см. п.3
			adapter.emit('regChanged', meta.target, next, {
				isRegistered: next.contacts.length > 0 && !next.terminated,
			});

			source.emit('regNotify', {
				aor: meta.target,
				contentType: resolvedContentType,
				xml,
				request,
				isFinal,
			});
		},

		onTerminated(meta, info: SubscriptionTerminatedPayload, source: any) {
			source.emit('regSubscribeTerminated', info, meta.target);
		},
	});
}
